import axios from 'axios';

/**
 * Validate an address using Google Maps Geocoding API
 * @param {string} address - The address to validate
 * @param {string} apiKey - Google Maps API key (optional, falls back to env var)
 * @returns {Promise<Object>} - Validation result with status and data
 */
export const validateAddress = async (address) => {
    try {
        const googleApiKey = process.env.GOOGLE_CLOUD_API_KEY;

        if (!googleApiKey) {
            // Return skipped result when API key is not available
            return {
                isValid: true,
                status: 'SKIPPED',
                message: 'Address validation skipped - API key not configured',
                skipped: true,
            };
        }

        if (!address || typeof address !== 'string' || address.trim() === '') {
            throw new Error('Address is required and must be a non-empty string');
        }

        const response = await axios.get(
            'https://maps.googleapis.com/maps/api/geocode/json',
            {
                params: {
                    address: address.trim(),
                    key: googleApiKey,
                },
            },
        );

        const { status, results } = response.data;

        if (status === 'OK' && results.length > 0) {
            const firstResult = results[0];
            return {
                isValid: true,
                status: 'OK',
                formattedAddress: firstResult.formatted_address,
                location: firstResult.geometry.location,
                placeId: firstResult.place_id,
                addressComponents: firstResult.address_components,
                types: firstResult.types,
                allResults: results,
            };
        }

        if (status === 'ZERO_RESULTS') {
            return {
                isValid: false,
                status: 'ZERO_RESULTS',
                message: 'No results found for this address',
            };
        }

        if (status === 'OVER_QUERY_LIMIT') {
            throw new Error('Google Maps API quota exceeded');
        }

        if (status === 'REQUEST_DENIED') {
            throw new Error('Google Maps API request denied. Check your API key.');
        }

        if (status === 'INVALID_REQUEST') {
            throw new Error('Invalid request to Google Maps API');
        }

        return {
            isValid: false,
            status,
            message: 'Address validation failed',
        };

    } catch (error) {
        if (error.response) {
            // Server responded with error status
            throw new Error(`Google Maps API error: ${error.response.status} - ${error.response.statusText}`);
        } else if (error.request) {
            // Request was made but no response received
            throw new Error('Network error: Unable to reach Google Maps API');
        } else {
            // Something else happened
            throw error;
        }
    }
};

/**
 * Validate multiple addresses in batch
 * @param {string[]} addresses - Array of addresses to validate
 * @param {string} apiKey - Google Maps API key (optional)
 * @returns {Promise<Object[]>} - Array of validation results
 */
export const validateAddressesBatch = async (addresses, apiKey = null) => {
    if (!Array.isArray(addresses)) {
        throw new Error('Addresses must be an array');
    }

    const validationPromises = addresses.map(async (address) => {
        try {
            return await validateAddress(address, apiKey);
        } catch (error) {
            return {
                isValid: false,
                status: 'ERROR',
                message: error.message,
                originalAddress: address,
            };
        }
    });

    return Promise.all(validationPromises);
};

/**
 * Extract address components from Google Maps result
 * @param {Object} addressComponents - Address components from Google Maps API
 * @returns {Object} - Structured address components
 */
export const extractAddressComponents = (addressComponents) => {
    const components = {};

    addressComponents.forEach((component) => {
        const types = component.types;

        if (types.includes('street_number')) {
            components.streetNumber = component.long_name;
        }
        if (types.includes('route')) {
            components.streetName = component.long_name;
        }
        if (types.includes('locality')) {
            components.city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
            components.state = component.short_name;
            components.stateLong = component.long_name;
        }
        if (types.includes('postal_code')) {
            components.zipCode = component.long_name;
        }
        if (types.includes('country')) {
            components.country = component.short_name;
            components.countryLong = component.long_name;
        }
    });

    return components;
};
